/* eslint-disable sonarjs/no-duplicate-string */
import { isSupported } from 'system/libs/phoneValidator';
import Yup from 'yup';

import isEmailBlackListed from 'isEmailBlackListed';

const integer = ({ min, max, even, odd }) => {
    let validator = Yup.number()
        .max(max)
        .min(min)
        .integer();

    if (even) {
        validator = validator.test(
            'even',
            'Введите четное число',
            (value) => value % 2 === 0,
        );
    } else if (odd) {
        validator = validator.test(
            'odd',
            'Введите нечетное число',
            (value) => value % 2 !== 0,
        );
    }

    return validator;
};

const requiredInteger = (params) => {
    return integer(params).required();
};

const password = (text?: string) =>
    Yup.string().matches(
        /^[A-Za-z0-9!"#$%&()*+,-.:;<=>?@[\]^_`{}~]*[0-9a-zA-Z]+[A-Za-z0-9!"#$%&()*+,-.:;<=>?@[\]^_`{}~]*$/,
        text || 'Пароль содержит недопустимые символы',
    );

const serverName = (text?: string) =>
    Yup.string().matches(
        /^([a-zA-Z0-9\-_.]+)$/,
        text ||
            'Имя может состоять только из\u00A0латинских букв и\u00A0цифр, символов "-", "_" и "."',
    );

const volumeName = (text?: string) =>
    Yup.string().matches(
        /^([a-zA-Z0-9-]+)$/,
        text ||
            'Имя может состоять только из\u00A0латинских букв и\u00A0цифр и символа "-"',
    );

const autoPlanName = (text?: string) =>
    Yup.string()
        .min(3)
        .max(100)
        .matches(
            /^[A-Za-z0-9!"#$%&()*+,-.:;<=>?@[\]^_`{}~]*$/,
            text || 'Имя содержит недопустимые символы',
        );

const appName = (names) => {
    return serverName()
        .required()
        .test({
            message: 'Имя должно быть уникальным',
            test: (value) => {
                return !names.includes(value);
            },
        });
};

const prohibitedStrings = ({
    message = 'Введен недопустимый текст',
    strings,
    additionalSchema,
}) =>
    (additionalSchema || Yup.string()).test({
        name: 'prohibitedStrings',
        message,
        test: (value) => !strings.includes(value),
    });

const CONTAINER_NAME_MAX_LENGTH = 24;
const containerName = (text?: string) =>
    Yup.string()
        .matches(
            /^[a-z]([a-z0-9-]*)$/,
            text ||
                'Имя должно начинаться с\u00A0буквы, может состоять только из\u00A0латинских строчных букв, цифр и\u00A0символов:\u00A0-',
        )
        .max(CONTAINER_NAME_MAX_LENGTH);

const backupName = (text?: string) =>
    Yup.string().matches(
        /^[A-Za-z0-9_-]+[A-Za-z0-9\-_.]*$/,
        text ||
            'Имя бэкапа может состоять только из латинских букв и цифр, символов "-", "_" и "."',
    );

const databaseInstanceName = (text?: string) =>
    Yup.string().matches(
        /^[A-Za-z0-9_-]+[\s?#@]*[A-Za-z0-9_-]+$/,
        text || 'Недопустимое имя базы данных',
    );

const DB_NAME_LIMITS = {
    MIN: 1,
    MAX: 16,
};
const databaseName = (message = 'Недопустимое имя базы данных') =>
    prohibitedStrings({
        message,
        strings: [
            'mysql',
            'information_schema',
            'performance_schema',
            'admin',
            'local',
            'config',
            'postgres',
            'os_admin',
        ],
        additionalSchema: Yup.string()
            .required()
            .min(DB_NAME_LIMITS.MIN)
            .max(DB_NAME_LIMITS.MAX)
            .matches(/^[A-Za-z0-9_-]+[\s?#@]*[A-Za-z0-9_-]+$/, message),
    });

const databaseClickhouseName = (message = 'Недопустимое имя базы данных') =>
    prohibitedStrings({
        message,
        strings: ['os_admin', 'default'],
        additionalSchema: Yup.string()
            .required()
            .min(DB_NAME_LIMITS.MIN)
            .max(DB_NAME_LIMITS.MAX)
            .matches(/^[A-Za-z][A-Za-z0-9_]+$/, message),
    });

const DB_USER_NAME_LIMITS = DB_NAME_LIMITS;
const databaseUserName = (message = 'Недопустимое имя пользователя') =>
    prohibitedStrings({
        message,
        strings: ['os_admin', 'root', 'postgres'],
        additionalSchema: Yup.string()
            .required()
            .min(DB_NAME_LIMITS.MIN)
            .max(DB_NAME_LIMITS.MAX)
            .matches(/^[^\s|'|"|;|`|,|/|\\|А-я]+$/, message),
    });

const DB_PASSWORD_LIMITS = {
    MIN: 8,
    MAX: 32,
};
const databasePassword = (text?: string) =>
    Yup.string()
        .matches(
            /^[A-Za-z0-9!"#$%&()*+,-.:;<=>?@[\]^_`{}~]*[0-9a-zA-Z]+[A-Za-z0-9!"#$%&()*+,-.:;<=>?@[\]^_`{}~]*$/,
            text || 'Пароль содержит недопустимые символы',
        )
        .required()
        .min(DB_PASSWORD_LIMITS.MIN)
        .max(DB_PASSWORD_LIMITS.MAX);

const windowsPassword = () => {
    const group1 = /[a-z]/;
    const group2 = /[A-Z]/;
    const group3 = /[0-9]/;
    const group4 = /[~!@#$%^&*_\-+=`|\\(){}[\]:;"'<>,.?/]/;

    return Yup.string()
        .test({
            test: (value) => {
                return (
                    (group1.test(value) &&
                        group2.test(value) &&
                        group3.test(value)) ||
                    (group1.test(value) &&
                        group2.test(value) &&
                        group4.test(value)) ||
                    (group2.test(value) &&
                        group3.test(value) &&
                        group4.test(value)) ||
                    (group1.test(value) &&
                        group3.test(value) &&
                        group4.test(value))
                );
            },
            message:
                'Пароль может быть от 8 до 32 символов, должен содержать символы из трех следующих категорий: [A-Z], [a-z], [0-9], [~!@#$%^&*_-+=`|\\(){}[]:;"\'<>,.?/]',
        })
        .required()
        .min(8)
        .max(32);
};

const storageAccountName = (text?: string) =>
    Yup.string()
        .matches(
            /^[A-z0-9]([A-z0-9_\-.]+)$/,
            text ||
                'Имя должно начинаться с\u00A0буквы или\u00A0цифры, может состоять только из\u00A0латинских букв, цифр и\u00A0символов:\u00A0.\u00A0-\u00A0_',
        )
        .min(4)
        .max(74);

const phone = (text?: string) => {
    const str = Yup.string();

    return str.matches(
        /^((8|\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d\- ]{7,10}$/,
        text || 'Некорректно указан телефон',
    );
};

Yup.addMethod(Yup.string, 'phoneCountry', function() {
    return this.test({
        message: 'Подтверждение личности из данной страны недоступно',
        test: (value) => {
            return isSupported(value);
        },
    });
});

const PASSWORD_LIMITS = {
    MIN: 8,
    MAX: 40,
};

const email = (text?: string) => Yup.string().email(text);

const newUserEmail = (text?: string) =>
    email(text).test({
        message: 'Регистрация с данного email невозможна',
        test: (value) => !isEmailBlackListed(value),
    });

const humanName = (text?: string) =>
    Yup.string().matches(
        /^[\w\sа-я-]+$/i,
        text || 'Имя может состоять только из букв, цифр и дефиса',
    );

const imageName = (text?: string) =>
    Yup.string().matches(
        /^[\w\s.а-яё-]+$/i,
        text || 'Имя может состоять только из букв, цифр и дефиса',
    );

const projectName = (text = PROJECT_NAME_VALIDATION_ERROR_TEXT) =>
    Yup.string().matches(/^[a-z0-9][a-z0-9_-]{2,61}[a-z0-9]$/, text);

const PROJECT_NAME_VALIDATION_ERROR_TEXT =
    'Имя проекта должно состоять только из строчных букв латинского алфавита и цифр, быть длинной от 4 до 60 символов. Допустимо использование символов "_", "-" (за исключением первого и последнего символов).';

const userPassword = (text?: string) =>
    Yup.string()
        .matches(
            /^[a-zA-Z0-9!@#$%^&*()-_+=;:,./?|`~[\]{}]{8,40}$/,
            text ||
                'Пароль может быть от 8 до 40 символов, должен содержать латинские буквы, цифры или символы: ? ! ~ @ # $ % ^ & _ - + * = ; : , . / \\ | ` [ ] { } ( )',
        )
        .min(PASSWORD_LIMITS.MIN)
        .max(PASSWORD_LIMITS.MAX)
        .required();

const commonName = serverName;

const latinLetters = (text?: string) =>
    Yup.string().matches(
        /^[a-zA-Z]*$/,
        text || 'Допустимы только латинские буквы',
    );

const latinLettersAndDigits = (text?: string) =>
    Yup.string().matches(
        /^[a-zA-Z0-9]*$/,
        text || 'Допустимы только цифры и латинские буквы',
    );

const latinLettersDigitsSomeSymbols = (text?: string) =>
    Yup.string().matches(
        /^[a-zA-Z0-9-_.]*$/,
        text ||
            'Допустимы только цифры, латинские буквы и символы "-", "_" и "."',
    );

const latinLettersDigitsSymbols = (text?: string) =>
    Yup.string().matches(
        /^[a-zA-Z0-9!@#$%^&*()-_+=;:,./?|`~[\]{}]*$/,
        text ||
            'Допустимы только цифры, латинские буквы и символы ? ! ~ @ # $ % ^ & _ - + * = ; : , . / \\\\ | ` [ ] { } ( )',
    );

const saharaClusterName = (text?: string) => {
    return Yup.string().matches(
        /^(([a-zA-Z]|[a-zA-Z][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z]|[A-Za-z][A-Za-z0-9-]*[A-Za-z0-9])$/,
        text ||
            'Имя может состоять только из\u00A0латинских букв и\u00A0цифр, символов "-" и "."',
    );
};

const inn = () =>
    Yup.string().test({
        message: 'ИНН должен состоять из 10 или 12 цифр',
        test: (value) => {
            return (
                value.match(/^\d+$/) &&
                (value.length === 12 || value.length === 10)
            );
        },
    });

const ogrn = () =>
    Yup.string().matches(/^(\d+)$/, 'ОГРН должен состоять из цифр');

const rub = ({ min = 0, max = 1000000 }) => {
    let validator = Yup.number()
        .max(max)
        .min(min)
        .required();

    validator = validator.test(
        'fractionalPart',
        'На копейки отводится от 0 до 2 знаков',
        (value) => {
            return (
                value !== undefined &&
                !!String(value).match(/^\d+((\.|,)\d\d?)?$/)
            );
        },
    );

    return validator;
};

export const ValidationRules = {
    integer,
    rub,
    requiredInteger,
    humanName,
    serverName,
    saharaClusterName,
    volumeName,
    imageName,
    appName,
    containerName,
    storageAccountName,
    password,
    prohibitedStrings,
    databaseInstanceName,
    databaseName,
    databaseClickhouseName,
    databaseUserName,
    databasePassword,
    windowsPassword,
    autoPlanName,
    phone,
    email,
    newUserEmail,
    userPassword,
    backupName,
    projectName,
    commonName,
    latinLetters,
    latinLettersAndDigits,
    latinLettersDigitsSomeSymbols,
    latinLettersDigitsSymbols,
    PROJECT_NAME_VALIDATION_ERROR_TEXT,
    DB_NAME_LIMITS,
    DB_USER_NAME_LIMITS,
    DB_PASSWORD_LIMITS,
    PASSWORD_LIMITS,
    CONTAINER_NAME_MAX_LENGTH,
    inn,
    ogrn,
};
